if (!window.__bundleGrindSelectorsInitialized) {
  window.__bundleGrindSelectorsInitialized = true;

  const API_VERSION_FALLBACK = '2025-10';
  const TOKEN_PLACEHOLDER = 'PASTE_YOUR_STOREFRONT_TOKEN_HERE';
  const ROOT_SELECTOR = '.bundle-grind-root';
  const COMPONENT_LIMIT = 10;

  /** @type {Map<string, any>} Cache GraphQL results per variant ID */
  const _bundleCache = new Map();

  function normalizeText(value) {
    return (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  function isCoffeeProduct(product) {
    if (product?.grindMeta?.value) return true;

    const type = normalizeText(product?.productType);
    return type.includes('cafe');
  }

  function parseOptions(value) {
    return (value || 'Whole Bean, Espresso, Filter, French Press')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function getVariantGid(numericId) {
    return `gid://shopify/ProductVariant/${numericId}`;
  }

  async function fetchBundleComponents({ variantId, token, apiVersion }) {
    const cacheKey = `${variantId}`;
    if (_bundleCache.has(cacheKey)) {
      return _bundleCache.get(cacheKey);
    }

    const response = await fetch(`/api/${apiVersion || API_VERSION_FALLBACK}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: `
          query BundleComponents($id: ID!) {
            node(id: $id) {
              ... on ProductVariant {
                id
                requiresComponents
                components(first: ${COMPONENT_LIMIT}) {
                  nodes {
                    quantity
                    productVariant {
                      id
                      title
                      product {
                        title
                        handle
                        productType
                        grindMeta: metafield(namespace: "shopify", key: "grind-size") {
                          value
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          id: getVariantGid(variantId),
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors?.length) {
      throw new Error(data.errors.map((error) => error.message).join('; '));
    }

    const result = data?.data?.node || null;
    _bundleCache.set(cacheKey, result);
    return result;
  }

  function getExistingSelections(form) {
    const selections = new Map();

    form?.querySelectorAll('select[name^="properties["]').forEach((select) => {
      if (select.name && select.value) {
        selections.set(select.name, select.value);
      }
    });

    return selections;
  }

  function buildPropertyName(baseLabel, component) {
    const qty = Number(component.quantity || 1);
    const suffix = qty > 1 ? ` (x${qty})` : '';
    return `${baseLabel} · ${component.productVariant.product.title}${suffix}`;
  }

  function renderBundleSelectors(root, components) {
    const form = root.closest('form');
    const grindLabel = root.dataset.grindLabel || 'Grind size';
    const propertyLabel = root.dataset.grindPropertyLabel || 'Grind Size';
    const options = parseOptions(root.dataset.grindOptions);
    const required = root.dataset.grindRequired === 'true';
    const previousSelections = getExistingSelections(form);

    const wrapper = document.createElement('div');
    wrapper.className = 'bundle-grind-fields';

    const title = document.createElement('p');
    title.className = 'grind-field__label';
    title.textContent = grindLabel;
    wrapper.appendChild(title);

    if (components.length > 1) {
      const help = document.createElement('p');
      help.className = 'bundle-grind-fields__help';
      help.textContent = 'Puedes elegir una molienda distinta para cada café del pack.';
      wrapper.appendChild(help);
    }

    components.forEach((component, index) => {
      const field = document.createElement('div');
      field.className = 'bundle-grind-field';

      const selectId = `${root.id}-component-${index}`;
      const propertyName = buildPropertyName(propertyLabel, component);
      const savedValue = previousSelections.get(`properties[${propertyName}]`);

      const label = document.createElement('label');
      label.className = 'bundle-grind-field__product';
      label.setAttribute('for', selectId);
      label.textContent =
        component.productVariant.product.title +
        (component.quantity > 1 ? ` (x${component.quantity})` : '');

      const select = document.createElement('select');
      select.id = selectId;
      select.className = 'grind-select';
      select.name = `properties[${propertyName}]`;

      if (required) {
        select.required = true;
      }

      options.forEach((optionValue) => {
        const option = document.createElement('option');
        option.value = optionValue;
        option.textContent = optionValue;

        if (savedValue && savedValue === optionValue) {
          option.selected = true;
        }

        select.appendChild(option);
      });

      field.appendChild(label);
      field.appendChild(select);
      wrapper.appendChild(field);
    });

    root.replaceChildren(wrapper);
    root.hidden = false;

    if (form) attachSubmitSync(form);
  }

  /* ------------------------------------------------------------------ */
  /*  Sync grind selections to cart note_attributes so they appear      */
  /*  in the Shopify admin order even for bundle component line items.  */
  /* ------------------------------------------------------------------ */
  function collectBundleGrinds(form) {
    const attrs = {};
    form.querySelectorAll('.bundle-grind-field select').forEach((sel) => {
      const match = sel.name.match(/^properties\[(.+)\]$/);
      if (match && sel.value) {
        attrs[match[1]] = sel.value;
      }
    });
    return attrs;
  }

  async function pushNoteAttributes(attrs) {
    if (!Object.keys(attrs).length) return;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      });
    } catch (e) {
      console.warn('[bundle-grind] note_attributes sync failed', e);
    }
  }

  function attachSubmitSync(form) {
    if (form.__bundleGrindSyncAttached) return;
    form.__bundleGrindSyncAttached = true;

    form.addEventListener('submit', () => {
      const attrs = collectBundleGrinds(form);
      pushNoteAttributes(attrs);
    });
  }

  function hideBundleSelectors(root) {
    root.replaceChildren();
    root.hidden = true;
  }

  function toggleSingleSelector(root, shouldShow) {
    const form = root.closest('form');
    const singleField = form?.querySelector('[data-single-grind-field]');
    if (!singleField) return;

    singleField.hidden = !shouldShow;

    singleField.querySelectorAll('select, input, textarea, button').forEach((el) => {
      el.disabled = !shouldShow;
    });
  }

  async function updateRoot(root, variantId) {
    const token = root.dataset.storefrontToken;
    const apiVersion = root.dataset.storefrontApiVersion || API_VERSION_FALLBACK;

    if (!token || token === TOKEN_PLACEHOLDER) {
      hideBundleSelectors(root);
      toggleSingleSelector(root, true);
      return;
    }

    if (!variantId) {
      hideBundleSelectors(root);
      toggleSingleSelector(root, true);
      return;
    }

    try {
      const bundleVariant = await fetchBundleComponents({ variantId, token, apiVersion });
      const components = bundleVariant?.components?.nodes || [];
      const coffeeComponents = bundleVariant?.requiresComponents
        ? components.filter((component) => isCoffeeProduct(component?.productVariant?.product))
        : [];

      if (coffeeComponents.length > 0) {
        renderBundleSelectors(root, coffeeComponents);
        toggleSingleSelector(root, false);
      } else {
        hideBundleSelectors(root);
        toggleSingleSelector(root, true);
      }
    } catch (error) {
      console.error('[bundle-grind-selectors]', error);
      hideBundleSelectors(root);
      toggleSingleSelector(root, true);
    }
  }

  function initRoot(root) {
    if (!(root instanceof HTMLElement)) return;

    const form = root.closest('form');
    if (!form) return;

    const variantInput = form.querySelector('input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) return;

    if (root.dataset.bundleGrindReady !== 'true') {
      root.dataset.bundleGrindReady = 'true';

      variantInput.addEventListener('change', () => {
        updateRoot(root, variantInput.value);
      });
    }

    updateRoot(root, variantInput.value);
  }

  function refreshRoots(scope = document) {
    const roots = [];

    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
      roots.push(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => initRoot(root));
  }

  window.refreshBundleGrindSelectors = refreshRoots;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refreshRoots());
  } else {
    refreshRoots();
  }

  document.addEventListener('variant:update', (event) => {
    const target = event.target;
    const scope =
      target instanceof HTMLElement
        ? target.closest('.shopify-section, dialog, product-card') || document
        : document;

    refreshRoots(scope);
  });

  document.addEventListener('bundle-grind:refresh', (event) => {
    const container = event.detail?.container;

    if (container instanceof Element) {
      refreshRoots(container);
    }
  });

  /* ------------------------------------------------------------------ */
  /*  MutationObserver — auto-init any .bundle-grind-root that appears  */
  /*  in the DOM (handles morph, quick-add, AJAX section rendering).    */
  /* ------------------------------------------------------------------ */
  (function observeDOM() {
    if (typeof MutationObserver === 'undefined') return;

    let pending = false;

    function scan() {
      pending = false;
      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        if (root instanceof HTMLElement && root.dataset.bundleGrindReady !== 'true') {
          initRoot(root);
        }
      });
    }

    const observer = new MutationObserver(() => {
      /* Debounce: batch rapid mutations into a single scan pass */
      if (!pending) {
        pending = true;
        requestAnimationFrame(scan);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  })();
}